import { RefCallback, useCallback, useRef } from 'react';

import { animatePromised } from '~/utilities/ponyfills';

import constants from '~/sass/consts.module.scss';

type TBreakpoints = {
  isSmallMobileView: boolean;
  isMediumMobileView: boolean;
  isMobileView: boolean;
};

/**
 * `PropertyIndexedKeyframes` or a function returning `PropertyIndexedKeyframes`
 * that gets passed element's height, width and screen width
 */
type TExtendedPropertyIndexedKeyframes =
  | PropertyIndexedKeyframes
  | ((args: {
      width: number;
      height: number;
      breakpoints: TBreakpoints;
    }) => PropertyIndexedKeyframes);

interface IUseAnimateElementArgs {
  options?: KeyframeAnimationOptions;
  mountKeyframes?: TExtendedPropertyIndexedKeyframes;
  animationKeyframes?: TExtendedPropertyIndexedKeyframes;
}

interface IUseAnimateElementState<Type> {
  ref: RefCallback<Type>;
  animate: (
    beforeAnimation?: () => void | Promise<void>,
  ) => Promise<Animation | null>;
}

const defaultAnimationOptions: KeyframeAnimationOptions = {
  iterations: 1,
  easing: 'ease-in-out',
};

const windowWidthToBreakpoints = (windowWidth: number): TBreakpoints => {
  return {
    isSmallMobileView: windowWidth <= Number(constants.smallMobileView),
    isMediumMobileView: windowWidth <= Number(constants.mediumMobileView),
    isMobileView: windowWidth <= Number(constants.mobileView),
  } as const;
};

export function useAnimateElement<Type extends HTMLElement>(
  props: IUseAnimateElementArgs,
): IUseAnimateElementState<Type> {
  const elementRef = useRef<Type | null>(null);
  const propsRef = useRef(props);
  /**
   * Assign the element to elementRef and perform mount animation if it was specified
   */
  const elementRefMountCallback: RefCallback<Type> = useCallback((element) => {
    const { options, mountKeyframes } = propsRef.current;
    if (!element) return;
    elementRef.current = element;
    if (mountKeyframes) {
      const keyframes =
        typeof mountKeyframes === 'function'
          ? mountKeyframes({
              width: element.clientWidth,
              height: element.clientHeight,
              breakpoints: windowWidthToBreakpoints(window.innerWidth),
            })
          : mountKeyframes;
      element.animate(
        keyframes,
        options
          ? {
              ...defaultAnimationOptions,
              ...options,
            }
          : defaultAnimationOptions,
      );
    }
  }, []);

  const animate = useCallback(
    async (beforeAnimateOut?: () => void | Promise<void>) => {
      if (beforeAnimateOut) {
        await beforeAnimateOut();
      }
      const { options, animationKeyframes } = propsRef.current;

      if (animationKeyframes && elementRef.current) {
        const element = elementRef.current;
        const keyframes =
          typeof animationKeyframes === 'function'
            ? animationKeyframes({
                width: element.clientWidth,
                height: element.clientHeight,
                breakpoints: windowWidthToBreakpoints(window.innerWidth),
              })
            : animationKeyframes;

        return animatePromised(
          element,
          keyframes,
          options
            ? { ...defaultAnimationOptions, ...options }
            : defaultAnimationOptions,
        );
      }
      return Promise.resolve(null);
    },
    [],
  );

  return {
    ref: elementRefMountCallback,
    animate,
  };
}
